import { createContext, useEffect, useState, type ReactNode } from 'react';
import type { TeamCreate, TeamState, Team } from '@/types/team';
import { teamService } from '@/services/teamService';
// import { toaster } from '@/components/ui/toaster';
// import { useTranslation } from 'react-i18next';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAuth } from '@/hooks/useAuth';

// --- FIX 1: Correct the function signatures in the type definition ---
export type TeamContextType = TeamState & {
  createTeam: (data: TeamCreate) => Promise<any>;
  switchTeam: (teamId: string) => void;
  getTeams: () => Promise<void>; // Renamed from refreshTeams for consistency
  updateTeam: (teamId: string, data: Partial<TeamCreate>) => Promise<void>; // Added missing parameters
  deleteTeam: (teamId: string) => Promise<void>; // Added missing parameter
};

export const TeamContext = createContext<TeamContextType | undefined>(undefined);

const initialState: TeamState = {
  isLoading: false,
  teams: [],
  selectedTeam: null,
};

export const TeamProvider = ({ children }: { children: ReactNode }) => {
  const [teamState, setTeamState] = useState<TeamState>(initialState);
  const { handleError } = useApiErrorHandler();
  const { isAuthenticated } = useAuth();
  // const { t } = useTranslation(['teams']);

  const createTeam = async (data: TeamCreate) => {
    setTeamState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await teamService.createTeam(data);
      // Refresh teams after creating a new one
      await getTeams(); // This call is correct
      setTeamState(prev => ({ ...prev, isLoading: false }));
      return response;
    } catch (err) {
      handleError(err);
      setTeamState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  // --- Standardized on the name `getTeams` ---
  const getTeams = async () => {
    setTeamState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await teamService.getTeams();
      const newTeams = response.data || [];
      if (newTeams) {
        const currentSelectedId = teamState.selectedTeam?.id;
        const newSelectedTeam = newTeams.find((team: Team) => team.id === currentSelectedId) || newTeams[0] || null;
        setTeamState(prev => ({
          ...prev,
          isLoading: false,
          teams: newTeams,
          selectedTeam: newSelectedTeam,
        }));
      }
    } catch (err) {
      console.error('Error getting teams:', err);
      setTeamState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const updateTeam = async (teamId: string, data: Partial<TeamCreate>) => {
    setTeamState(prev => ({ ...prev, isLoading: true }));
    try {
      await teamService.updateTeam(teamId, data);
      await getTeams();
    } catch (err) {
      handleError(err);
      setTeamState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const switchTeam = (teamId: string) => {
    const newTeam = teamState.teams.find(team => team.id === teamId);
    if (newTeam) {
      setTeamState(prev => ({ ...prev, selectedTeam: newTeam }));
    }
  };

  const deleteTeam = async (teamId: string) => {
    setTeamState(prev => ({ ...prev, isLoading: true }));
    try {
      await teamService.deleteTeam(teamId);
      await getTeams();
    } catch (err) {
      handleError(err)
      setTeamState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setTeamState(initialState); // optional: clear state when logged out
      return;
    }

    const initTeam = async () => {
      try {
        await getTeams();
      } catch (err: any) {
        handleError(err);
      }
    };

    initTeam();
  }, [isAuthenticated]);

  // --- FIX 3: Ensure the value object matches the corrected type ---
  const value: TeamContextType = {
    ...teamState,
    switchTeam,
    createTeam,
    updateTeam,
    deleteTeam,
    getTeams, // Use getTeams here to match the function name
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};